import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { DateRangeFilterFields } from 'src/components/DateRangeFilterFields';
import { DateFilterInputValue, DateRangeChangeHandler } from 'src/components/DateRangeFilterFields/types';
import { AccountListFilters } from '../../types';
import { useTranslation } from 'react-i18next';

export interface AccountListFiltersBoxProps {
  filtersForm: UseFormReturn<AccountListFilters>;
  forceRefetch: () => void;
}

export const AccountListFiltersBox = ({ filtersForm }: AccountListFiltersBoxProps) => {
  const { register, setValue, reset, control } = filtersForm;
  const { t } = useTranslation();

  const dateValueControl = useController({
    name: 'dateValue',
    control,
  });
  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  const handleRangeChange: DateRangeChangeHandler = (startDate, endDate) => {
    startDateControl.field.onChange(startDate);
    endDateControl.field.onChange(endDate);
  };

  const handleClear = () => {
    // Resetar todos os campos do formulário
    reset({
      name: '',
      access_type: undefined,
      start_date: '',
      end_date: '',
      dateValue: DateFilterInputValue.SEVEN,
    });

    // Garantir que o componente DateRangeFilterFields receba o valor atualizado
    dateValueControl.field.onChange(DateFilterInputValue.SEVEN);

    // Opcionalmente, calcular as datas correspondentes a "últimos 7 dias"
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    startDateControl.field.onChange(sevenDaysAgo.toISOString().split('T')[0]);
    endDateControl.field.onChange(today.toISOString().split('T')[0]);
  };

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>{t('pages.accountlist.components.filter_box.heading')}</Heading>
      </Flex>

      <Flex direction={{ base: 'column' }} gap='small' rowGap='micro'>
        <SimpleGrid gap='deka' alignItems='flex-end' columns={{ base: 1, md: 2, lg: 4, '10xl': 5 }} rowGap='xxsmall' flexGrow={1}>
          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.accountlist.components.filter_box.label_register')}</Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('name'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.accountlist.components.filter_box.label_access')}</Text>
            <SelectField {...register('access_type')}>
              <option value=''>{t('pages.accountlist.components.filter_box.option_all')}</option>
            </SelectField>
          </InputGroup>
        </SimpleGrid>

        <DateRangeFilterFields
          onRangeChange={handleRangeChange}
          startDate={startDateControl.field.value}
          endDate={endDateControl.field.value}
          dateValue={dateValueControl.field.value}
          onDateValueChange={dateValueControl.field.onChange}
          inputName='partner-actions-date'
          startDateRootProps={{ w: { base: '100%', md: 'fit-content' } }}
          endDateRootProps={{ w: { base: '100%', md: 'fit-content' } }}
        />

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={handleClear}>
          {t('pages.accountlist.components.filter_box.button_clear')}
        </Button>
      </Flex>
    </Box>
  );
};
